import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, Save, User, Mail, Phone, Calendar, ClipboardList, Stethoscope, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPORTS = [
    "", "Lacrosse", "Football", "Basketball", "Soccer", "Baseball", "Hockey",
    "Volleyball", "Track & Field", "Swimming", "Wrestling", "Tennis",
    "Gymnastics", "Crossfit", "General Fitness", "Other",
];

const TRACKS = [
    { v: "", label: "—" },
    { v: "elementary", label: "Elementary" },
    { v: "middle_school", label: "Middle School" },
    { v: "high_school", label: "High School" },
    { v: "college", label: "College" },
    { v: "adult", label: "Adult" },
    { v: "pro", label: "Pro" },
];

/**
 * AthleteEditModal — Phase A first-class athlete profile editor.
 *
 * Owner/coach can edit every field on the athlete except `account_user_id`
 * (parent linkage). Email here OVERRIDES the parent's email for emails
 * scoped to THIS athlete (workout reminders, attendance receipts).
 * Billing emails still go to the parent.
 */
export default function AthleteEditModal({ open, onClose, athlete, onSaved }) {
    const [f, setF] = useState(() => seed(athlete));
    const [saving, setSaving] = useState(false);

    // Re-seed if a new athlete prop comes in.
    useEffect(() => {
        if (open) setF(seed(athlete));
    }, [open, athlete]);

    if (!open) return null;

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!f.first_name?.trim()) {
            toast.error("First name is required");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                first_name: f.first_name.trim(),
                last_name: (f.last_name || "").trim(),
                dob: (f.dob || "").trim(),
                email: (f.email || "").trim(),
                phone: (f.phone || "").trim(),
                primary_sport: f.primary_sport || "",
                track: f.track || "",
                status: f.status || "active",
                person_type: f.person_type || "athlete",
                internal_note: (f.internal_note || "").trim(),
                health_notes: (f.health_notes || "").trim(),
                coach_notes: (f.coach_notes || "").trim(),
            };
            const r = await axios.patch(
                `${API}/coach/athletes/${athlete.athlete_id}`,
                payload,
                { withCredentials: true },
            );
            toast.success("Athlete updated", {
                description: `${r.data?.athlete?.first_name || ""} ${r.data?.athlete?.last_name || ""}`.trim(),
            });
            onSaved?.(r.data?.athlete);
            onClose?.();
        } catch (err) {
            toast.error("Couldn't save", { description: err?.response?.data?.detail || err?.message });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/85 flex items-start justify-center overflow-y-auto p-4"
            onClick={onClose}
            data-testid="athlete-edit-modal"
        >
            <form
                className="bg-zinc-950 border border-zinc-800 w-full max-w-2xl my-8"
                onClick={(e) => e.stopPropagation()}
                onSubmit={submit}
            >
                {/* Header */}
                <div className="px-5 py-3.5 border-b border-zinc-900 flex items-center justify-between sticky top-0 bg-zinc-950 z-10">
                    <div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red">Edit athlete</div>
                        <div className="font-display text-xl tracking-wide uppercase">{athlete?.first_name} {athlete?.last_name}</div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-zinc-400 hover:text-white p-1"
                        data-testid="athlete-edit-close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Sections */}
                <div className="p-5 space-y-6">
                    {/* IDENTITY */}
                    <Section icon={User} title="Identity">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Field label="First name *">
                                <input value={f.first_name || ""} onChange={(e) => setF({ ...f, first_name: e.target.value })} className={inp} data-testid="ae-first-name" />
                            </Field>
                            <Field label="Last name">
                                <input value={f.last_name || ""} onChange={(e) => setF({ ...f, last_name: e.target.value })} className={inp} data-testid="ae-last-name" />
                            </Field>
                            <Field label="Date of birth">
                                <input type="date" value={f.dob || ""} onChange={(e) => setF({ ...f, dob: e.target.value })} className={inp} data-testid="ae-dob" />
                            </Field>
                            <Field label="Status">
                                <select value={f.status || "active"} onChange={(e) => setF({ ...f, status: e.target.value })} className={inp} data-testid="ae-status">
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive (hidden from roster)</option>
                                </select>
                            </Field>
                            <Field label="Primary sport">
                                <select value={f.primary_sport || ""} onChange={(e) => setF({ ...f, primary_sport: e.target.value })} className={inp} data-testid="ae-sport">
                                    {SPORTS.map((s) => <option key={s} value={s}>{s || "—"}</option>)}
                                </select>
                            </Field>
                            <Field label="Track">
                                <select value={f.track || ""} onChange={(e) => setF({ ...f, track: e.target.value })} className={inp} data-testid="ae-track">
                                    {TRACKS.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                                </select>
                            </Field>
                            <Field label="Person type" hint="'Both' = parent who also trains. Auto-set for self-athletes on parent signup.">
                                <select value={f.person_type || "athlete"} onChange={(e) => setF({ ...f, person_type: e.target.value })} className={inp} data-testid="ae-person-type">
                                    <option value="athlete">Athlete (kid)</option>
                                    <option value="parent">Parent (no training)</option>
                                    <option value="both">Both (parent who trains)</option>
                                </select>
                            </Field>
                        </div>
                    </Section>

                    {/* CONTACT */}
                    <Section icon={Mail} title="Contact" hint="Email/phone here OVERRIDE the parent's contact for emails scoped to this athlete (workout reminders, attendance). Billing emails still go to the parent.">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Field label="Email (athlete's own)">
                                <input type="email" value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} className={inp} placeholder="leave blank to use parent's" data-testid="ae-email" />
                            </Field>
                            <Field label="Phone">
                                <input value={f.phone || ""} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inp} placeholder="optional" data-testid="ae-phone" />
                            </Field>
                        </div>
                    </Section>

                    {/* HEALTH (family-visible) */}
                    <Section icon={Stethoscope} title="Health notes" hint="Visible to coaches AND family — keep medical-safety information here.">
                        <textarea
                            value={f.health_notes || ""}
                            onChange={(e) => setF({ ...f, health_notes: e.target.value })}
                            rows={2}
                            className={inp + " resize-none"}
                            placeholder="Allergies, asthma, recent injuries, anything safety-critical"
                            data-testid="ae-health-notes"
                        />
                    </Section>

                    {/* COACH-ONLY */}
                    <Section icon={Lock} title="Coach-only notes" hint="NEVER shown to the family. Use for internal coaching reminders, dynamics with siblings, scheduling preferences, etc.">
                        <div className="space-y-3">
                            <Field label="Internal note">
                                <textarea
                                    value={f.internal_note || ""}
                                    onChange={(e) => setF({ ...f, internal_note: e.target.value })}
                                    rows={2}
                                    className={inp + " resize-none"}
                                    placeholder="Short coach memo (e.g. 'Slow to warm up. Loves competitive drills.')"
                                    data-testid="ae-internal-note"
                                />
                            </Field>
                            <Field label="Coach notes (long form)">
                                <textarea
                                    value={f.coach_notes || ""}
                                    onChange={(e) => setF({ ...f, coach_notes: e.target.value })}
                                    rows={4}
                                    className={inp + " resize-none"}
                                    placeholder="Long-form coach observations, programming notes, progress thoughts"
                                    data-testid="ae-coach-notes"
                                />
                            </Field>
                        </div>
                    </Section>
                </div>

                {/* Footer */}
                <div className="px-5 py-3.5 border-t border-zinc-900 flex items-center justify-end gap-2 sticky bottom-0 bg-zinc-950">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        className="btn-ghost-tactical text-xs"
                        data-testid="ae-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="btn-tactical text-xs"
                        data-testid="ae-save"
                    >
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save changes
                    </button>
                </div>
            </form>
        </div>
    );
}


function seed(athlete) {
    return {
        first_name: athlete?.first_name || "",
        last_name: athlete?.last_name || "",
        dob: athlete?.dob || "",
        email: athlete?.email || "",
        phone: athlete?.phone || "",
        primary_sport: athlete?.primary_sport || "",
        track: athlete?.track || "",
        status: athlete?.status || "active",
        person_type: athlete?.person_type || "athlete",
        internal_note: athlete?.internal_note || "",
        health_notes: athlete?.health_notes || "",
        coach_notes: athlete?.coach_notes || "",
    };
}


// =============== Layout primitives ===============

const inp = "w-full bg-black border border-zinc-700 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:border-brand-red";

function Section({ icon: Icon, title, hint, children }) {
    return (
        <section>
            <div className="flex items-center gap-2 mb-2">
                {Icon && <Icon className="w-4 h-4 text-brand-red" />}
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 font-semibold">{title}</div>
            </div>
            {hint && <div className="text-[11px] text-zinc-500 mb-3 max-w-prose">{hint}</div>}
            {children}
        </section>
    );
}

function Field({ label, children, hint }) {
    return (
        <label className="block">
            <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 block mb-1">{label}</span>
            {children}
            {hint && <span className="text-[10px] text-zinc-500 block mt-1">{hint}</span>}
        </label>
    );
}
